import { useTranslation } from 'react-i18next';
import { CollapsibleNote } from '../CollapsibleNote';
import { useAnalyticsCustomers } from '../../../hooks/useAnalyticsCustomers';
import { useAnalyticsMachines } from '../../../hooks/useAnalyticsMachines';
import './CustomersMachinesTab.css';

interface CustomersMachinesTabProps {
  year: number;
}

export const CustomersMachinesTab: React.FC<CustomersMachinesTabProps> = ({ year }) => {
  const { t, i18n } = useTranslation();
  const { customers, loading: loadingCustomers, error: errorCustomers } = useAnalyticsCustomers(year);
  const { machines, loading: loadingMachines, error: errorMachines } = useAnalyticsMachines(year);

  const numberFormatter = new Intl.NumberFormat(i18n.language);
  const hoursFormatter = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  const formatDate = (iso: string): string => dateFormatter.format(new Date(`${iso}T00:00:00`));

  return (
    <div className="analytics-customers-machines">
      <CollapsibleNote variant="warn" title={t('analytics.customersMachines.countryTitle')}>
        {t('analytics.customersMachines.countryBanner')}
      </CollapsibleNote>

      <div className="analytics-panels-grid">
        <div className="analytics-panel">
          <h3>{t('analytics.customersMachines.topCustomersTitle')}</h3>
          {loadingCustomers && <p className="analytics-coming-soon">{t('app.loading')}</p>}
          {!loadingCustomers && (errorCustomers || !customers) && (
            <p className="analytics-error">{t('analytics.loadError')}</p>
          )}
          {!loadingCustomers && customers && customers.top.length === 0 && (
            <p className="analytics-coming-soon">{t('analytics.customersMachines.noCustomers')}</p>
          )}
          {!loadingCustomers && customers && customers.top.length > 0 && (
            <div className="analytics-tech-table-container">
              <table className="analytics-tech-table">
                <thead>
                  <tr>
                    <th>{t('analytics.customersMachines.customerColumns.name')}</th>
                    <th className="n">{t('analytics.customersMachines.customerColumns.reports')}</th>
                    <th className="n">{t('analytics.customersMachines.customerColumns.workHours')}</th>
                    <th className="n">{t('analytics.customersMachines.customerColumns.partsUsed')}</th>
                    <th className="n">{t('analytics.customersMachines.customerColumns.lastReportDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.top.map((row) => (
                    <tr key={row.customerId}>
                      <td>{row.name}</td>
                      <td className="n">{numberFormatter.format(row.reports)}</td>
                      <td className="n">{hoursFormatter.format(row.workHours)}</td>
                      <td className="n">{numberFormatter.format(row.partsUsed)}</td>
                      <td className="n">{formatDate(row.lastReportDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="analytics-panel">
          <h3>{t('analytics.customersMachines.topMachinesTitle')}</h3>
          {loadingMachines && <p className="analytics-coming-soon">{t('app.loading')}</p>}
          {!loadingMachines && (errorMachines || !machines) && (
            <p className="analytics-error">{t('analytics.loadError')}</p>
          )}
          {!loadingMachines && machines && machines.top.length === 0 && (
            <p className="analytics-coming-soon">{t('analytics.customersMachines.noMachines')}</p>
          )}
          {!loadingMachines && machines && machines.top.length > 0 && (
            <div className="analytics-tech-table-container">
              <table className="analytics-tech-table">
                <thead>
                  <tr>
                    <th>{t('analytics.customersMachines.machineColumns.serialNumber')}</th>
                    <th>{t('analytics.customersMachines.machineColumns.customerName')}</th>
                    <th className="n">{t('analytics.customersMachines.machineColumns.reports')}</th>
                    <th className="n">{t('analytics.customersMachines.machineColumns.workHours')}</th>
                    <th className="n">{t('analytics.customersMachines.machineColumns.partsUsed')}</th>
                  </tr>
                </thead>
                <tbody>
                  {machines.top.map((row) => (
                    <tr key={row.machineId}>
                      <td>{row.serialNumber}</td>
                      <td>{row.customerName ?? '—'}</td>
                      <td className="n">{numberFormatter.format(row.reports)}</td>
                      <td className="n">{hoursFormatter.format(row.workHours)}</td>
                      <td className="n">{numberFormatter.format(row.partsUsed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="analytics-panel analytics-panel-future">
        <h3>{t('analytics.customersMachines.familyViewTitle')}</h3>
        <p className="analytics-panel-sub">{t('analytics.customersMachines.familyViewText')}</p>
      </div>
    </div>
  );
};

export default CustomersMachinesTab;
